export type PersonCue = {
  id: string;
  name: string;
  feature: string;
  avatar: string;
};

type RandomSource = () => number;

const VIVID_WORDS = [
  "lantern", "otter", "velvet", "comet", "cinnamon", "bridge", "violin", "pepper",
  "anchor", "orchid", "castle", "lemon", "telescope", "badger", "ribbon", "volcano",
  "ginger", "balcony", "trumpet", "marble", "compass", "tulip", "lighthouse", "coconut",
  "backpack", "penguin", "feather", "thunder", "saffron", "staircase", "piano", "magnet",
  "suitcase", "willow", "waterfall", "pumpkin", "helmet", "dolphin", "blanket", "rocket",
  "teapot", "peacock", "diamond", "carousel", "avocado", "windmill", "typewriter", "jellyfish",
  "snowflake", "basket", "dragon", "umbrella", "camera", "strawberry", "saxophone", "hourglass",
  "parrot", "mountain", "keyhole", "sunflower",
];

const EVERYDAY_WORDS = [
  "acorn", "airplane", "apron", "beehive", "bicycle", "binoculars", "blueberry", "bookshelf",
  "bowling ball", "bubble", "cactus", "campfire", "canoe", "chessboard", "clover", "coffee mug",
  "cookie", "crayon", "crown", "drum", "elevator", "firefly", "flashlight", "fountain",
  "garden gate", "giraffe", "guitar", "hammock", "harp", "hedgehog", "ice skate", "igloo",
  "jigsaw", "kettle", "kite", "koala", "ladder", "ladybug", "mailbox", "microphone",
  "moonstone", "mushroom", "notebook", "octopus", "paintbrush", "paper boat", "pineapple", "pocket watch",
  "raincoat", "roller skate", "sandcastle", "seashell", "skateboard", "snowman", "soap bubble", "spoon",
  "stethoscope", "tangerine", "toy train", "watering can", "whistle", "xylophone", "yarn", "zebra",
];

export const CONCEPT_WORDS = [
  "balance", "belonging", "bravery", "change", "choice", "curiosity", "distance", "doubt",
  "effort", "fairness", "freedom", "friendship", "gravity", "habit", "harmony", "hope",
  "humor", "idea", "justice", "kindness", "knowledge", "luck", "memory", "momentum",
  "patience", "peace", "promise", "purpose", "rhythm", "silence", "surprise", "time",
  "trust", "victory", "warmth", "wisdom",
];

export const ALL_WORDS = [...VIVID_WORDS, ...EVERYDAY_WORDS, ...CONCEPT_WORDS];

export const PERSON_NAMES = [
  "Maya", "Theo", "Nora", "Felix", "Amina", "Jonah", "Clara", "Mateo",
  "Iris", "Hugo", "Leila", "Owen", "Sofia", "Eli", "Zara", "Miles",
  "Priya", "Caleb", "Noemi", "Darius", "Mei", "Rafael", "Ada", "Samir",
  "Lena", "Isaac", "Nia", "Gabriel", "Yara", "Emmett", "Rina", "Tomas",
  "Amara", "Leo", "Ines", "Malik", "June", "Nico", "Talia", "Bennett",
  "Anika", "Rory", "Selene", "Dev", "Mira", "Ellis", "Layla", "Andre",
  "Keira", "Omar", "Lucia", "Jasper", "Ayana", "Ren", "Fatima", "Micah",
  "Elena", "Kwame", "Cleo", "Arun", "Bianca", "Soren", "Imani", "Pablo",
];

const DISTINCTIVE_FEATURES = [
  "round amber glasses", "bright green scarf", "star-shaped earrings", "curled moustache",
  "purple cat-eye glasses", "blue bow tie", "sunflower hair clip", "red knit beanie",
  "silver lightning brooch", "yellow polka-dot scarf", "turquoise headphones", "crescent-moon earrings",
  "orange suspenders", "tiny cactus pin", "striped necktie", "heart-shaped glasses",
  "violet headband", "gold pocket square", "rainbow watchband", "denim cap",
  "pearl hair clip", "green butterfly pin", "round cobalt earrings", "cherry-red glasses",
  "plaid bow tie", "silver leaf barrette", "coral bandana", "constellation scarf",
  "wooden bead necklace", "navy sailor cap", "bright enamel ring", "lavender ribbon",
];

const SUBTLE_FEATURES = [
  "thin striped watchband", "small silver lapel pin", "freckle near the left eyebrow", "narrow tortoiseshell glasses",
  "tiny compass necklace", "pale blue shirt collar", "single copper earring", "braided leather bracelet",
  "soft gray pocket square", "small wave-shaped pin", "fine dotted headband", "dark green glasses cord",
  "miniature book charm", "slim burgundy tie", "white-stitched cap brim", "small flower cufflink",
  "two-tone glasses arms", "woven teal bracelet", "tiny gold star pin", "cream scarf knot",
  "narrow patterned lanyard", "matte black ring", "silver collar bar", "blue thread bracelet",
  "small geometric earring", "subtle checkered tie", "leaf-shaped hair pin", "thin coral necklace",
  "tiny music-note badge", "brown glasses chain", "small sunburst brooch", "indigo watch face",
];

const AVATARS = [
  "👩🏽‍🦱", "👨🏻", "👩🏻‍🦰", "👨🏾‍🦱", "🧕🏽", "👨🏼", "👩🏿", "👨🏽‍🦲",
  "👩🏼", "👨🏿‍🦳", "👩🏾", "👨🏻‍🦰", "👩🏻", "👨🏽", "👩🏽‍🦳", "👨🏼‍🦱",
  "👩🏿‍🦲", "👨🏾", "👩🏼‍🦱", "👨🏻‍🦳", "🧔🏽", "👩🏾‍🦰", "👨🏿", "👩🏻‍🦳",
];

export function shuffleItems<T>(items: readonly T[], random: RandomSource = Math.random) {
  const copy = [...items];
  for (let index = copy.length - 1; index > 0; index--) {
    const target = Math.floor(random() * (index + 1));
    [copy[index], copy[target]] = [copy[target], copy[index]];
  }
  return copy;
}

export function pickFreshItems(
  pool: readonly string[],
  count: number,
  recent: readonly string[] = [],
  random: RandomSource = Math.random,
) {
  const uniquePool = [...new Set(pool)];
  const size = Math.max(0, Math.min(Math.floor(count), uniquePool.length));
  const recentSet = new Set(recent);
  const unseen = shuffleItems(
    uniquePool.filter((item) => !recentSet.has(item)),
    random,
  );
  const fallback = shuffleItems(
    uniquePool.filter((item) => recentSet.has(item)),
    random,
  );
  const items = [...unseen, ...fallback].slice(0, size);
  const historyLimit = Math.min(
    Math.max(0, uniquePool.length - size),
    size * 3,
  );
  const nextRecent = [
    ...items,
    ...recent.filter((item) => !items.includes(item) && uniquePool.includes(item)),
  ].slice(0, historyLimit);
  return { items, nextRecent };
}

export function createWordRound(
  level: number,
  count: number,
  recent: readonly string[] = [],
  random: RandomSource = Math.random,
) {
  const normalizedLevel = Math.max(1, Math.min(10, level));
  const concretePool = normalizedLevel <= 3 ? VIVID_WORDS : [...VIVID_WORDS, ...EVERYDAY_WORDS];
  const conceptCount = normalizedLevel >= 7
    ? Math.min(Math.ceil((normalizedLevel - 6) / 1.5), Math.max(0, count - 2))
    : 0;
  const concepts = pickFreshItems(CONCEPT_WORDS, conceptCount, recent, random).items;
  const concrete = pickFreshItems(
    concretePool.filter((item) => !concepts.includes(item)),
    count - concepts.length,
    recent,
    random,
  ).items;
  const items = shuffleItems([...concepts, ...concrete], random);
  const poolSize = concretePool.length + (conceptCount ? CONCEPT_WORDS.length : 0);
  const nextRecent = [
    ...items,
    ...recent.filter((item) => !items.includes(item)),
  ].slice(0, Math.min(Math.max(0, poolSize - items.length), items.length * 3));
  return { items, nextRecent };
}

export function createPeopleRound({
  level,
  count,
  recentNames = [],
  recentFeatures = [],
  random = Math.random,
}: {
  level: number;
  count: number;
  recentNames?: readonly string[];
  recentFeatures?: readonly string[];
  random?: RandomSource;
}) {
  const names = pickFreshItems(PERSON_NAMES, count, recentNames, random);
  const normalizedLevel = Math.max(1, Math.min(10, level));
  const subtleCount = normalizedLevel >= 6
    ? Math.min(Math.ceil((normalizedLevel - 5) / 2), Math.max(0, count - 2))
    : 0;
  const subtle = pickFreshItems(SUBTLE_FEATURES, subtleCount, recentFeatures, random).items;
  const distinctive = pickFreshItems(
    DISTINCTIVE_FEATURES.filter((feature) => !subtle.includes(feature)),
    count - subtle.length,
    recentFeatures,
    random,
  ).items;
  const features = shuffleItems([...subtle, ...distinctive], random);
  const featurePoolSize = DISTINCTIVE_FEATURES.length + (subtleCount ? SUBTLE_FEATURES.length : 0);
  const nextRecentFeatures = [
    ...features,
    ...recentFeatures.filter((feature) => !features.includes(feature)),
  ].slice(0, Math.min(Math.max(0, featurePoolSize - features.length), features.length * 3));
  const avatars = shuffleItems(AVATARS, random);
  const people = names.items.map((name, index) => {
    const feature = features[index];
    return {
      id: `${name.toLowerCase()}-${feature.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`,
      name,
      feature,
      avatar: avatars[index % avatars.length],
    };
  });
  return {
    people,
    nextRecentNames: names.nextRecent,
    nextRecentFeatures,
  };
}

function chunksAreUnique(value: string, size: number) {
  const chunks = value.match(new RegExp(`.{1,${size}}`, "g")) ?? [];
  return new Set(chunks).size === chunks.length;
}

export function createUniqueChunkNumber(
  length: number,
  recent: readonly string[] = [],
  random: RandomSource = Math.random,
) {
  const safeLength = Math.max(4, Math.min(18, Math.floor(length)));
  for (let attempt = 0; attempt < 500; attempt++) {
    const value = Array.from({ length: safeLength }, (_, index) =>
      String(index === 0 ? 1 + Math.floor(random() * 9) : Math.floor(random() * 10)),
    ).join("");
    if (
      !recent.includes(value) &&
      [2, 3, 4].every((size) => chunksAreUnique(value, size))
    ) return value.split("");
  }
  throw new Error("Could not create a unique number round");
}

export function orderDueCards<T extends { dueAt: string }>(
  cards: readonly T[],
  random: RandomSource = Math.random,
) {
  return shuffleItems(cards, random).sort(
    (left, right) => Date.parse(left.dueAt) - Date.parse(right.dueAt),
  );
}

export function choiceExtrasForLevel(level: number) {
  return Math.min(5, 2 + Math.ceil(Math.max(1, Math.min(10, level)) / 3));
}
